import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from '@mui/x-data-grid';
import { getCurrentTerms } from '../projectTypeUtils';

const SelectedTrackersModal = ({
    open,
    onClose,
    selectedRows,
    trackerTypes
}) => {
    const TERMS = getCurrentTerms()
    // Process the data to create rows for the table
    const processTableData = () => {
        if (!selectedRows || !selectedRows.sections) return [];

        const tableRows = [];
        let rowId = 0;

        // Iterate through sections
        Object.entries(selectedRows.sections).forEach(([sectionKey, sectionData]) => {
            const sectionNumber = sectionKey.replace('section_', '');

            if (sectionData.selectedRows) {
                Object.entries(sectionData.selectedRows).forEach(([trackerId, trackerData]) => {
                    if (trackerData.piers && trackerData.piers.length > 0) {
                        // Get tracker type from the first pier
                        const firstPier = trackerData.piers[0];
                        const trackerTypeId = firstPier.tracker_type_id;

                        // Find the tracker type name from trackerTypes array
                        const trackerTypeInfo = trackerTypes.find(tt => tt.tracker_type_id === trackerTypeId);
                        const trackerTypeName = trackerTypeInfo ? trackerTypeInfo.tracker_type : `Type ${trackerTypeId}`;

                        tableRows.push({
                            id: rowId++,
                            section: sectionNumber,
                            trackerRow: firstPier.row,
                            trackerType: trackerTypeName
                        });
                    }
                });
            }
        });

        return tableRows;
    };

    // DataGrid columns definition
    const columns = [
        {
            field: 'section',
            headerName: TERMS.SECTION_HEADER,
            flex: 1,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: 'trackerRow',
            headerName: TERMS.ROW_HEADER,
            flex: 1,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: 'trackerType',
            headerName: `${TERMS.TRACKER_LABEL} Type`,
            flex: 2,
            headerAlign: 'center',
            align: 'center'
        },
    ];

    const rows = processTableData();
    const totalTrackers = selectedRows?.count || 0;

    // Calculate tracker type summary
    const trackerTypeSummary = () => {
        const summary = {};

        if (selectedRows && selectedRows.trackerTypes) {
            Object.entries(selectedRows.trackerTypes).forEach(([typeKey, typeData]) => {
                const typeId = typeKey.replace('trackerType_', '');
                const trackerTypeInfo = trackerTypes.find(tt => tt.tracker_type_id === parseInt(typeId));
                const trackerTypeName = trackerTypeInfo ? trackerTypeInfo.tracker_type : `Type ${typeId}`;

                if (!summary[trackerTypeName]) {
                    summary[trackerTypeName] = 0;
                }
                summary[trackerTypeName] += typeData.selectedCount || 0;
            });
        }

        return summary;
    };

    const typeSummary = trackerTypeSummary();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: '600px',
                    borderRadius: 2
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 1
            }}>
                <Typography variant="h6" fontWeight={600}>
                    Selected {TERMS.TRACKER_LABEL}(s)
                </Typography>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={onClose}
                    sx={{
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {/* Tracker Type Summary with Total */}
                <Box sx={{ px: 3, py: 2 }}>
                    {Object.entries(typeSummary).map(([typeName, count]) => (
                        <Box
                            key={typeName}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '45%',
                                mb: 1
                            }}
                        >
                            <Typography sx={{ flex: 1 }}>{typeName}</Typography>
                            <Typography sx={{ width: '30px', textAlign: 'center' }}>-</Typography>
                            <Typography sx={{ width: '50px', textAlign: 'left', paddingLeft: "10px" }}>{count}</Typography>
                        </Box>
                    ))}

                    {/* Total with proper alignment matching tracker types */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                    }}>
                        {/* Wrapper to match the 45% width for alignment */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '45%'
                        }}>
                            <Typography fontWeight={600} sx={{ flex: 1 }}>Total</Typography>
                            <Typography sx={{ width: '30px', textAlign: 'center' }}>-</Typography>
                            <Typography sx={{ width: '50px', textAlign: 'left', paddingLeft: "10px" }}>
                                {totalTrackers}
                            </Typography>
                        </Box>
                        <Typography sx={{ ml: -1 }}>
                            {TERMS.TRACKER_LABEL}(s)
                        </Typography>
                    </Box>

                </Box>

                {/* DataGrid Table */}
                <Box sx={{ height: 'auto', px: 3, pb: 2 }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        disableSelectionOnClick
                        hideFooter
                        autoHeight
                        disableColumnMenu
                        sx={{
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#f16253 !important',
                                color: 'white !important',
                                fontWeight: 600,
                            },
                            '& .MuiDataGrid-columnHeader': {
                                backgroundColor: '#f16253 !important',
                                color: 'white !important',
                                borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                                '&:last-child': {
                                    borderRight: 'none',
                                },
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                color: 'white !important',
                                fontWeight: 600,
                            },
                            '& .MuiDataGrid-cell': {
                                textAlign: 'center',
                                borderRight: '1px solid #e0e0e0',
                                '&:last-child': {
                                    borderRight: 'none',
                                },
                            },
                            '& .MuiDataGrid-footerContainer': {
                                borderTop: '1px solid #e0e0e0',
                            },
                            '& .MuiDataGrid-columnSeparator': {
                                display: 'none',
                            },
                            // Force header text to be white
                            '& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitleContainer': {
                                color: 'white !important',
                            },
                            // Remove the menu button
                            '& .MuiDataGrid-columnHeader--withRightBorder .MuiDataGrid-menuIcon': {
                                display: 'none',
                            },
                            '& .MuiDataGrid-menuIcon': {
                                display: 'none',
                            },
                        }}
                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default SelectedTrackersModal;